// Two users' notes on one visit (BlinkScribe AUTO-COMBINE same-user rule,
// incident 10-06 / recording 48693). Pure helpers, no React, no network.
//
// Background: BlinkScribe used to merge a new recording into ANY user's
// same-day note on the appointment. A note one provider had filed on the
// wrong chart swallowed the next provider's real visit. Now BlinkScribe only
// merges into a note the recording user owns; another user's note is left
// alone and reported as `other_user_note` ({ rowid, audiolink, user_id,
// user_name, ts }). The care UI asks before recording whether to add to that
// note (the explicit combine: category=combine, rowid, filename=audiolink),
// and the chart note panel says when the visit has another user's note.

import { isNoteDone, rowAppointmentId } from "./recordingCompletion";

const numId = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

// GET /v1/recording/appointment/{id}/combine-target -> { own, other, mode },
// or null for anything else (old server, error body): the caller then lets
// BlinkScribe decide, as before.
//
// A server with the start check (start_check: 1, bsfix-other-user-visit) also
// gives: signed (another user's newest SIGNED or posted note on the visit),
// visit (this appointment) and visitsToday (the patient's OTHER appointments
// today, each with `mine`: the recording user's provider, null = unknown).
export const combineTargetOf = (body) => {
  const b = body && typeof body === "object" && body.data && !Array.isArray(body.data) && body.data.mode
    ? body.data
    : body;
  if (!b || typeof b !== "object" || !("other_user_note" in b || "own_note" in b)) return null;
  const pick = (n) => (n && numId(n.rowid) && n.audiolink ? n : null);
  const startCheck = Number(b.start_check) === 1;
  const signed = b.other_user_signed_note;
  return {
    own: pick(b.own_note),
    other: pick(b.other_user_note),
    mode: String(b.mode || "flag"),
    startCheck,
    signed: startCheck && signed && numId(signed.rowid) && signed.status === "signed" ? signed : null,
    visit: startCheck && b.visit && typeof b.visit === "object" ? b.visit : null,
    visitsToday: startCheck && Array.isArray(b.patient_visits_today)
      ? b.patient_visits_today.filter((v) => v && numId(v.appointmentid))
      : [],
  };
};

// Ask only when this user has no note of their own on the visit today and
// someone else does (and the server is not in the old "merge" mode).
export const shouldAskCombine = (target) =>
  Boolean(target && target.mode === "flag" && !target.own && target.other);

// Who wrote the other note, for copy. Never a patient name.
export const otherNoteAuthor = (other) => {
  const n = String(other?.user_name ?? "").trim();
  return n || "Another user";
};

// "10:01 AM" from the note's server timestamp (server-local, America/Chicago),
// or "" when unparseable.
export const otherNoteTime = (other) => {
  const m = /(\d{1,2}):(\d{2})/.exec(String(other?.ts ?? "").split(/[ T]/)[1] || "");
  if (!m) return "";
  const h = Number(m[1]);
  if (!Number.isFinite(h) || h > 23) return "";
  return `${((h + 11) % 12) + 1}:${m[2]} ${h < 12 ? "AM" : "PM"}`;
};

export const combineChoiceCopy = (other) => {
  const who = otherNoteAuthor(other);
  const at = otherNoteTime(other);
  const cc = String(other?.chief_complaint ?? "").trim();
  return {
    title: "This visit already has a note",
    body: `${who} already has a note on this visit today${at ? ` (${at})` : ""}.`,
    // What that note is about: a note filed on the wrong chart (10-06) shows
    // another patient's complaint here.
    about: cc ? `Chief complaint in that note: "${cc}"` : "",
    question:
      `Add your recording to ${who}'s note, or keep a separate note? ` +
      "Two notes on one visit must be combined before either can be signed.",
    addLabel: `Add to ${who}'s note`,
    separateLabel: "Separate note",
  };
};

// The combine parameters startRecording takes (latestApp shape).
export const combineIntoApp = (other) =>
  other && numId(other.rowid) && other.audiolink ? { id: numId(other.rowid), audiolink: other.audiolink } : null;

// Finished notes of the same visit as `shown` (same appointment; any recorder)
// other than `shown` itself, newest first. rows: recording/details rows.
export const otherNotesOnVisit = (rows, shown) => {
  const appt = rowAppointmentId(shown);
  const shownId = numId(shown?.id);
  if (!appt || !shownId) return [];
  return (Array.isArray(rows) ? rows : [])
    .filter(
      (r) =>
        r &&
        numId(r.id) &&
        numId(r.id) !== shownId &&
        isNoteDone(r.transcriptionstatus) &&
        rowAppointmentId(r) === appt &&
        ["", "original", "combine"].includes(String(r.recording_type ?? "").trim().toLowerCase()),
    )
    .sort((a, b) => numId(b.id) - numId(a.id));
};

// One grey line for the chart note panel, "" when there is nothing to say.
export const otherNotesLine = (others) => {
  const list = Array.isArray(others) ? others : [];
  if (!list.length) return "";
  const names = [...new Set(list.map((r) => String(r.recorded_by ?? "").trim()).filter(Boolean))];
  const who = names.length ? ` by ${names.join(", ")}` : "";
  const what = list.length === 1 ? "another note" : `${list.length} other notes`;
  return `This visit also has ${what}${who}. See Note Review; combine before signing.`;
};

// Note Review list: "recorded by <name>" when another listed note is on the
// same visit (both are listed under the visit's provider), else "".
export const recorderTag = (notes, note) => {
  const who = String(note?.recorded_by ?? "").trim();
  const appt = rowAppointmentId(note);
  if (!who || !appt) return "";
  const sibling = (Array.isArray(notes) ? notes : []).some(
    (n) => n && n.id !== note.id && rowAppointmentId(n) === appt,
  );
  return sibling ? `recorded by ${who}` : "";
};

// ── Another user's SIGNED note on the visit (10-06, patient 4662) ──────────
// Appointment 889791 held provider A's signed note; its card had been
// reassigned to provider B, who also had two new visits of the patient that
// day. B's recording from the 889791 card would have become a separate note
// on A's visit, and signing it posts into A's Athena encounter. BlinkScribe
// never merges into a signed note, so the Add / Separate question above never
// fired. Now the start asks first, offering the user's own visit today.

// Ask when someone else's note on this visit is signed (or already posted)
// and this user has no note of their own there today. An own note means the
// user already chose this visit; it keeps the old behaviour.
export const shouldAskSigned = (target) =>
  Boolean(target && target.startCheck && !target.own && target.signed);

// Where the recording can go instead:
//   own     - the patient's active visits today with this user's provider
//   unknown - the user's provider is not known: every other active visit
//             today, for the user to recognise (each names its provider)
//   none    - no other visit of theirs today
export const visitChoices = (target) => {
  const here = numId(target?.visit?.appointmentid);
  const active = (Array.isArray(target?.visitsToday) ? target.visitsToday : []).filter(
    (v) => v && numId(v.appointmentid) && numId(v.appointmentid) !== here && v.active !== false,
  );
  const own = active.filter((v) => v.mine === true);
  if (own.length) return { kind: "own", visits: own };
  const unknown = active.filter((v) => v.mine === null || v.mine === undefined);
  if (unknown.length) return { kind: "unknown", visits: unknown };
  return { kind: "none", visits: [] };
};

// "11:20" -> "11:20 AM"; "" when unparseable.
export const visitTimeText = (hhmm) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(hhmm ?? "").trim());
  if (!m) return "";
  const h = Number(m[1]);
  if (!Number.isFinite(h) || h > 23) return "";
  return `${((h + 11) % 12) + 1}:${m[2]} ${h < 12 ? "AM" : "PM"}`;
};

// One visit as a button label: "11:20 AM visit · Jaime · checked in".
export const visitChoiceLabel = (v) => {
  const at = visitTimeText(v?.time);
  const who = String(v?.provider ?? "").trim();
  return [at ? `${at} visit` : `Visit #${numId(v?.appointmentid) || ""}`, who, v?.checked_in ? "checked in" : ""]
    .filter(Boolean)
    .join(" · ");
};

// Whose signed note it is: the signer (sign-off stamps signed_by_name; an MA
// may have recorded what a provider signed), else who recorded it.
export const signedNoteOwner = (signed) => {
  const signer = String(signed?.signed_by_name ?? "").trim();
  return signer || otherNoteAuthor(signed);
};

export const signedNoteCopy = (signed, choices) => {
  const who = signedNoteOwner(signed);
  const at = otherNoteTime(signed);
  const when = at ? ` (${at})` : "";
  const kind = choices?.kind || "none";
  const visits = Array.isArray(choices?.visits) ? choices.visits : [];
  const body = signed?.approved
    ? `${who} signed a note on this visit${when}.`
    : `${who}'s note on this visit${when} is already posted to Athena.`;
  let question = "";
  if (kind === "own" && visits.length === 1) {
    question = "This patient has a visit with you today. Record there instead:";
  } else if (kind === "own") {
    question = `This patient has ${visits.length} visits with you today. Record on one of them:`;
  } else if (kind === "unknown") {
    question = "Is one of this patient's other visits today yours? Record there instead:";
  } else {
    question = "This patient has no other visit with you today.";
  }
  return {
    title: `This visit already has ${who}'s signed note`,
    body,
    question,
    // exactly one own visit: the button names it
    visitLabel: (v) =>
      kind === "own" && visits.length === 1 ? `Record on my ${visitChoiceLabel(v)}` : visitChoiceLabel(v),
    allowSeparate: kind !== "own",
    separateLabel: "Record as a separate note on this visit",
    separateWarning:
      kind !== "own"
        ? `A separate note here stays on ${who}'s visit: signing it posts into ${who}'s encounter in Athena.`
        : "",
  };
};
